import { useEffect, useState } from "react";
import { useParams, useNavigate, NavLink, Outlet, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutGrid, Gavel, PieChart, FileText, CheckSquare, Users, Share2,
  ChevronsUpDown, Check, Menu, LogOut, Building2, Loader2, ArrowLeft, User, Calculator, BarChart3,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RoleBadge } from "@/components/badges";
import { LicenseGate } from "@/components/LicenseGate";

const NAV = [
  { to: "", label: "Oversikt", icon: LayoutGrid, end: true },
  { to: "nokkeltall", label: "Nøkkeltall", icon: BarChart3 },
  { to: "styrerom", label: "Styrerom", icon: Gavel },
  { to: "aksjehandbok", label: "Aksjeeierbok", icon: PieChart },
  { to: "simulering", label: "Simulering", icon: Calculator },
  { to: "dokumenter", label: "Dokumenter", icon: FileText },
  { to: "oppgaver", label: "Oppgaver", icon: CheckSquare },
  { to: "roller", label: "Roller", icon: Users },
  { to: "tilgang", label: "Tilgang", icon: Share2 },
];

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

function SidebarNav({ companyId, company, onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5">
        <Link to="/hjem"><Logo /></Link>
      </div>
      <div className="px-3 pb-2">
        <Link
          to="/hjem"
          onClick={onNavigate}
          data-testid="back-to-companies"
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Alle selskaper
        </Link>
        <Link
          to="/portefolje"
          onClick={onNavigate}
          data-testid="nav-portfolio"
          className="mt-1 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <PieChart className="h-3.5 w-3.5" strokeWidth={1.6} /> Min portefølje
        </Link>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-2" data-testid="sidebar-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={`/selskap/${companyId}/${item.to}`}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" strokeWidth={1.7} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      {company ? (
        <div className="border-t border-border/60 p-4">
          <p className="truncate text-sm font-semibold text-foreground">{company.name}</p>
          <p className="text-xs text-muted-foreground">Org.nr {company.org_number}</p>
          {company.my_role ? <div className="mt-2"><RoleBadge role={company.my_role} /></div> : null}
        </div>
      ) : null}
    </div>
  );
}

export function AppShell() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([api.get(`/companies/${companyId}`), api.get(`/companies`)])
      .then(([c, list]) => {
        if (!active) return;
        setCompany(c.data);
        setCompanies(list.data);
      })
      .catch(() => navigate("/hjem"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [companyId, navigate]);

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const refetchCompany = () => api.get(`/companies/${companyId}`).then((r) => setCompany(r.data));

  return (
    <div className="flex min-h-screen bg-secondary/30">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-border/60 bg-background lg:block">
        <SidebarNav companyId={companyId} company={company} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-border/60 bg-background/80 px-4 backdrop-blur-xl sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarNav companyId={companyId} company={company} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Company switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="company-switcher"
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:bg-muted"
              >
                <div className="grid h-7 w-7 place-items-center rounded-md bg-primary/10 text-primary">
                  <Building2 className="h-4 w-4" strokeWidth={1.7} />
                </div>
                <div className="hidden min-w-0 sm:block">
                  <p className="truncate text-sm font-semibold leading-tight">{company?.name}</p>
                  <p className="text-[11px] text-muted-foreground">{company?.my_role}</p>
                </div>
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Dine selskaper
              </DropdownMenuLabel>
              {companies.map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  data-testid={`switch-company-${c.id}`}
                  onClick={() => navigate(`/selskap/${c.id}`)}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">{c.my_role}</p>
                  </div>
                  {c.id === companyId ? <Check className="h-4 w-4 text-primary" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-1.5">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-muted">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar || undefined} />
                    <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile-link" onClick={() => navigate("/profil")}>
                  <User className="mr-2 h-4 w-4" /> Min profil
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}>
                  <LogOut className="mr-2 h-4 w-4" /> Logg ut
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <motion.main
          key={companyId}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="flex-1 p-5 sm:p-8 lg:p-10"
        >
          {company?.license && !company.license.signed ? (
            <LicenseGate companyId={companyId} onSigned={refetchCompany} />
          ) : null}
          <Outlet context={{ company, refetchCompany }} />
        </motion.main>
      </div>
    </div>
  );
}
